import { config } from '@src/config';

let _authToken: string | undefined;

/** Call once from a top-level component to keep the token in sync with the auth provider. */
export function setAuthToken(token: string | undefined): void {
  _authToken = token;
}

export function getAuthToken(): string | undefined {
  return _authToken;
}

export async function apiFetch<T>(
  endpoint: string,
  options?: { method?: string; body?: unknown },
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
    method: options?.method ?? 'GET',
    headers,
    body: options?.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: AbortSignal.timeout(config.api.timeout),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/** Like `apiFetch`, but a 404 resolves to `undefined` instead of throwing -- for endpoints where
 * "no record" is an expected, non-error outcome (e.g. a property/scope with no enrichment yet). */
export async function apiFetchOptional<T>(endpoint: string): Promise<T | undefined> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  const response = await fetch(`${config.api.baseUrl}${endpoint}`, {
    headers,
    signal: AbortSignal.timeout(config.api.timeout),
  });

  if (response.status === 404) return undefined;
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export function buildQueryString(params: Record<string, string | string[] | number | undefined>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else {
      searchParams.set(key, String(value));
    }
  }

  return searchParams.toString();
}
